import React from 'react';

// Format value for display based on unit and magnitude
const formatGaugeValue = (rawValue, unit) => {
  let displayValue = rawValue;
  let displayUnit = unit || '';

  // Handle percentunit (0.0-1.0) - convert to percentage for display
  if (unit === 'percentunit') {
    displayValue = rawValue * 100;
    displayUnit = 'percent';
  }

  // For percent unit: cap at 100 if value is reasonable, or format large numbers
  if (displayUnit === 'percent' || unit === 'percent') {
    if (displayValue > 100 && displayValue <= 10000) {
      // Might be 0-100 scale stored differently
      displayValue = Math.min(displayValue, 100);
    } else if (displayValue > 10000) {
      // Raw metric with wrong unit - format as short number
      displayUnit = '';
    }
  }

  // Format large numbers nicely
  if (Math.abs(displayValue) >= 1000000) {
    return (displayValue / 1000000).toFixed(2) + 'M';
  }
  if (Math.abs(displayValue) >= 1000) {
    return (displayValue / 1000).toFixed(2) + 'K';
  }

  // Default: 1-2 decimals based on value
  const decimals = displayValue === Math.floor(displayValue) ? 0 : 
    (Math.abs(displayValue) < 10 ? 2 : 1);
  return displayValue.toFixed(decimals);
};

const getDisplayUnit = (unit, rawValue) => {
  if (!unit) return '';
  if (unit === 'percentunit') return 'percent';
  if (unit === 'percent' && rawValue > 10000) return ''; // Raw data with wrong unit
  return unit;
};

const GaugeChart = ({ data, options = {}, fieldConfig = {} }) => {
  const rawValue = typeof data === 'number' ? data : 0;
  const unit = fieldConfig.defaults?.unit || '';
  const min = fieldConfig.defaults?.min ?? 0;
  const max = fieldConfig.defaults?.max ?? 100;

  // Value for gauge arc (0-100 scale)
  const arcValue = unit === 'percentunit' ? rawValue * 100 : rawValue;
  const percentage = Math.min(Math.max(((arcValue - min) / (Math.max(max - min, 0.001))) * 100, 0), 100);

  // Get thresholds
  const thresholds = fieldConfig.defaults?.thresholds?.steps || [
    { value: 0, color: 'green' },
    { value: 80, color: 'red' }
  ];

  const getColor = (val) => {
    const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
    for (let threshold of sortedThresholds) {
      if (val >= threshold.value) return threshold.color;
    }
    return thresholds[0]?.color || 'green';
  };

  const color = getColor(arcValue);
  const colorMap = {
    green: '#52c41a',
    yellow: '#faad14',
    red: '#ff4d4f',
    blue: '#1890ff'
  };
  const fillColor = colorMap[color] || color;

  const formattedValue = formatGaugeValue(rawValue, unit);
  const displayUnit = getDisplayUnit(unit, rawValue);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ position: 'relative', width: '180px', height: '110px' }}>
        <svg width="180" height="110" viewBox="0 0 180 110">
          <path 
            d="M 20 90 A 70 70 0 0 1 160 90" 
            fill="none" 
            stroke="#f0f0f0" 
            strokeWidth="18" 
            strokeLinecap="round" 
          />
          <path 
            d="M 20 90 A 70 70 0 0 1 160 90" 
            fill="none" 
            stroke={fillColor} 
            strokeWidth="18" 
            strokeLinecap="round" 
            strokeDasharray={`${(percentage / 100) * 220} 220`} 
          />
        </svg>
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -30%)', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: fillColor }}>
            {formattedValue}
          </div>
          {displayUnit && (
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px', textTransform: 'capitalize' }}>
              {displayUnit}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GaugeChart;